import React, { useEffect, useState } from "react";
import { FaMoon, FaSun } from "react-icons/fa";
import { HiOutlineMenuAlt3, HiOutlineX } from "react-icons/hi";
import { Link, useLocation } from "react-router-dom";
import NavigatationMenu from "../common/NavigationMenu";
import siteContent from "../../generated/site-content";

function HeaderContainer({ theme, onToggleTheme }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const NAVBARCLASS = {
    ulClass: "flex flex-wrap gap-x-4 gap-y-2 lg:gap-x-5",
    liClass: "",
    aClass:
      "py-1 text-xs font-medium theme-text-muted theme-link-plain sm:text-sm",
  };
  const MOBILE_NAVBARCLASS = {
    ulClass: "flex flex-col gap-5",
    liClass: "",
    aClass:
      "py-1 text-lg font-medium theme-text-soft theme-link-plain",
  };

  const NAVBARITEMS = siteContent.navigation;
  const themeToggleLabel =
    theme === "dark" ? "Switch to light mode" : "Switch to dark mode";
  const themeToggleIcon =
    theme === "dark" ? (
      <FaSun className="text-sm" />
    ) : (
      <FaMoon className="text-sm" />
    );
  const themeToggleButtonClass =
    "theme-icon-button inline-flex h-9 w-9 items-center justify-center rounded-full border";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  return (
    <>
      <div className="flex w-full items-start justify-between gap-4 border-b theme-border pb-4 lg:items-end">
        <Link to="/" className="theme-link-plain min-w-0 hover:opacity-80">
          <p className="theme-text-muted text-[0.65rem] font-semibold uppercase tracking-[0.28em]">
            {siteContent.branding.eyebrow}
          </p>
          <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-2">
            <span className="theme-text text-lg font-semibold tracking-tight sm:text-xl">
              {siteContent.branding.name}
            </span>
            <span className="theme-text-muted text-xs sm:text-sm">
              {siteContent.branding.role}
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            aria-label={themeToggleLabel}
            title={themeToggleLabel}
            onClick={onToggleTheme}
            className={themeToggleButtonClass}
          >
            {themeToggleIcon}
          </button>
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            aria-label={
              menuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            onClick={() => setMenuOpen((open) => !open)}
            className="theme-icon-button mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border"
          >
            {menuOpen ? (
              <HiOutlineX className="text-lg" />
            ) : (
              <HiOutlineMenuAlt3 className="text-lg" />
            )}
          </button>
        </div>

        <div className="hidden items-center gap-3 lg:flex lg:self-center">
          <button
            type="button"
            aria-label={themeToggleLabel}
            title={themeToggleLabel}
            onClick={onToggleTheme}
            className={themeToggleButtonClass}
          >
            {themeToggleIcon}
          </button>
          <NavigatationMenu navBarCss={NAVBARCLASS} navBarItems={NAVBARITEMS} />
        </div>
      </div>

      <div
        className={`theme-overlay fixed inset-0 z-40 backdrop-blur-[1px] transition-opacity duration-300 lg:hidden ${
          menuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />

      <aside
        id="mobile-navigation"
        className={`theme-page fixed inset-y-0 left-0 z-50 flex w-[min(22rem,88vw)] flex-col border-r theme-border px-5 pb-6 pt-5 shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!menuOpen}
      >
        <div className="flex items-start justify-between gap-4 border-b theme-border pb-5">
          <Link
            to="/"
            className="theme-link-plain min-w-0 hover:opacity-80"
            onClick={() => setMenuOpen(false)}
          >
            <p className="theme-text-muted text-[0.65rem] font-semibold uppercase tracking-[0.28em]">
              {siteContent.branding.eyebrow}
            </p>
            <div className="mt-2 flex flex-col gap-1">
              <span className="theme-text text-lg font-semibold tracking-tight">
                {siteContent.branding.name}
              </span>
              <span className="theme-text-muted text-sm">
                {siteContent.branding.role}
              </span>
            </div>
          </Link>
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setMenuOpen(false)}
            className="theme-icon-button inline-flex h-10 w-10 items-center justify-center rounded-full border"
          >
            <HiOutlineX className="text-lg" />
          </button>
        </div>

        <div className="pt-6">
          <NavigatationMenu
            navBarCss={MOBILE_NAVBARCLASS}
            navBarItems={NAVBARITEMS}
            onItemClick={() => setMenuOpen(false)}
          />
        </div>
      </aside>
    </>
  );
}
export default HeaderContainer;
